'use client';

import { useMemoizedFn } from 'ahooks';
import { useTranslations } from 'next-intl';
import { type FC, type SyntheticEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { BaseLineInfo } from '@/app/components/icons/BaseLineInfo';
import { BaseLineRecreate } from '@/app/components/icons/BaseLineRecreate';
import { BasicTextarea } from '@/app/components/shared/BasicTextarea';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { TemplateImgCropModal } from '@/app/components/shared/ImgCropModals/TemplateImgCropModal';
import { MediaDropZone } from '@/app/components/shared/MediaUploader/MediaDropZone';
import { VoiceSelectionDialog } from '@/app/components/shared/VoiceSelectionDialog';
import { Button } from '@/app/components/ui/button';
import { toast } from '@/app/components/ui/use-toast';
import { RESOLUTION_MAP } from '@/app/constants';
import { useDevice, useGlobalModal, useRecreate } from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import type {
  AudioUploadFile,
  CreationInfo,
  CreationWithTask,
  FormAudioPrompt,
  FormImagePrompt,
  ImagePromptWithSize,
  ImageRange,
  OfflineAvatarInputMode,
  TaskInfo,
} from '@/app/types';
import { EllipsisActionTypeEnum } from '@/app/types';
import {
  countLipSyncTextWeightedLength,
  estimateLipSyncTextDurationSeconds,
  getImageSize,
  isAdmissionTemplateTask,
  isNoRecreateTask,
  isPostDisabledModelVersion,
  isRecreateModelVersionUnavailable,
  normalizeDiscreteVideoDurationToSeconds,
  reportEvent,
  trimLipSyncTextToMaxWeightedLength,
} from '@/app/utils';
import { convertToolTask, cropImage, handleChangeImageRange, offlineAvatarHasBuiltInAudio } from '@/app/utils/client';
import { CreationActionButtons } from '../../_components/TaskCard/components/CreationActionButtons';
import { ActionButtonType, type DisplayTaskState } from '../../_components/TaskCard/types';
import { getCompactTaskHistoryStateMessage } from '../../_components/TaskHistory';
import { ToolAudioUpload } from '../_components/ToolAudioUpload';
import { ToolFormSubmitButton } from '../_components/ToolFormSubmitButton';
import { ToolMediaUploadHeader } from '../_components/ToolMediaUploadHeader';
import { useRunTempUploadBatch } from '../_hooks/use-run-temp-upload-batch';
import { useToolForm } from '../_hooks/use-tool-form';
import { createPromptListHelpers, createToolAudioPromptFromUploadFile } from '../_utils';
import { getToolTempAudioPendingItems, getToolTempImagesPendingItems } from '../_utils/tool-temp-upload-items';
import {
  OFFLINE_AVATAR_RESOLUTION_OPTIONS,
  OfflineAvatarAssetSelector,
  OfflineAvatarCombinedSetting,
  OfflineAvatarHistoryDetailButton,
  OfflineAvatarHistoryPanel,
  OfflineAvatarPlaceholderPreview,
  type OfflineAvatarResolution,
  OfflineAvatarTaskStatePreview,
  OfflineAvatarTimelineEditor,
} from './_components';
import {
  createTimelineAction,
  DEFAULT_TIMELINE_ACTION_DURATION,
  getImageAspectRatio,
  getOfflineAvatarCreationPreview,
  hasTimelineActionOutOfBounds,
  hasTimelineActionOverlap,
  isTimelineTextValid,
  OFFLINE_AVATAR_CROP_OPTIONS,
  OFFLINE_AVATAR_DEFAULT_CROP_RATIO,
  resolveOfflineAvatarHistoryCreation,
} from './_utils';

const MAX_IMAGE_FILE_SIZE = 50 * 1024 * 1024;
const MAX_IMAGE_SIZE = RESOLUTION_MAP['4k'];
const MAX_AUDIO_FILE_SIZE = 20 * 1024 * 1024;
const MIN_AUDIO_DURATION = 5;
const MAX_AUDIO_DURATION = 30 * 60;
const MIN_TEXT_LENGTH = 4;
const MAX_TEXT_LENGTH = 5000;
type SelectedImageResult = {
  blob: Blob;
  width: number;
  height: number;
  range: ImageRange;
  cropRatio: number | 'free';
};

type CropResolver = (value: SelectedImageResult | { cancelled: true }) => void;

const getSupportedResolution = (value?: string): OfflineAvatarResolution => {
  const normalized = value?.toLowerCase() as OfflineAvatarResolution | undefined;
  return normalized && OFFLINE_AVATAR_RESOLUTION_OPTIONS.includes(normalized) ? normalized : '720p';
};

const getImagePreview = (imagePrompt?: FormImagePrompt | null) =>
  imagePrompt?.prompt?.selectedImagePreview ||
  imagePrompt?.prompt?.selectedImage ||
  imagePrompt?.prompt?.sourceImagePreview ||
  imagePrompt?.prompt?.sourceImage ||
  '';

const selectedImageRangeIsSame = (range: ImageRange, selectedImageRange?: ImageRange) =>
  Boolean(selectedImageRange && range.every((value, index) => value === selectedImageRange[index]));

const getPreviewAspectRatio = (imagePrompt?: FormImagePrompt | null, fallbackAspectRatio = 9 / 16) => {
  const prompt = imagePrompt?.prompt;
  if (prompt?.selectedImageWidth && prompt?.selectedImageHeight) {
    return getImageAspectRatio(prompt.selectedImageWidth, prompt.selectedImageHeight, fallbackAspectRatio);
  }
  if (prompt?.selectedImageRange) {
    const width = prompt.selectedImageRange[2] - prompt.selectedImageRange[0];
    const height = prompt.selectedImageRange[3] - prompt.selectedImageRange[1];
    if (width > 0 && height > 0) return getImageAspectRatio(width, height, fallbackAspectRatio);
  }
  return fallbackAspectRatio;
};

const cropImageToBlob = async (sourceImagePreview: string, range: ImageRange): Promise<Blob | null> => {
  const cropUrl = await cropImage(sourceImagePreview, range);
  if (!cropUrl) return null;

  try {
    const response = await fetch(cropUrl);
    return await response.blob();
  } finally {
    URL.revokeObjectURL(cropUrl);
  }
};

const OfflineAvatarForm: FC = () => {
  const t = useTranslations();
  const storeApi = useStoreApi();
  const scrollableTargetId = useId();
  const { settings, updateSettings } = useToolForm('offline_avatar');
  const [selectedHistoryCreation, setSelectedHistoryCreation] = useState<CreationWithTask | null>(null);
  const [selectedHistoryTask, setSelectedHistoryTask] = useState<TaskInfo | null>(null);
  const [selectedHistoryStatusCreation, setSelectedHistoryStatusCreation] = useState<CreationWithTask | null>(null);
  const [selectedHistoryDisplayTaskState, setSelectedHistoryDisplayTaskState] = useState<DisplayTaskState | null>(null);
  const [isVoiceDialogOpen, setIsVoiceDialogOpen] = useState(false);
  const [cropModalImageSource, setCropModalImageSource] = useState<ImagePromptWithSize | null>(null);
  const [cropRatio, setCropRatio] = useState<number | 'free'>('free');
  const [avatarImageLayout, setAvatarImageLayout] = useState({ url: '', aspectRatio: 9 / 16 });
  const cropResolverRef = useRef<CropResolver | null>(null);
  const cropRejectRef = useRef<((reason?: unknown) => void) | null>(null);
  const { open: openActionDialog } = useGlobalModal('actionDialog');
  const { open: openDeleteDialog } = useGlobalModal('deleteDialog');
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');
  const { open: openPostsModal } = useGlobalModal('postsModal');
  const { isMobile } = useDevice();
  const { handleRecreate } = useRecreate();
  const {
    offlineAvatarFormParams,
    updateOfflineAvatarFormParams,
    updateCreation,
    imagePrompts,
    audioPrompts,
    offlineAvatar,
    taskList,
    removeTask,
    updateTask,
    userInfo,
    isTeamSpace,
  } = useStore((state) => ({
    offlineAvatarFormParams: state.offlineAvatarFormParams,
    updateOfflineAvatarFormParams: state.updateOfflineAvatarFormParams,
    updateCreation: state.updateCreation,
    imagePrompts: state.offlineAvatarFormParams.imagePrompts,
    audioPrompts: state.offlineAvatarFormParams.audioPrompts,
    offlineAvatar: state.offlineAvatarFormParams.offlineAvatar,
    taskList: state.taskList,
    removeTask: state.removeTask,
    updateTask: state.updateTask,
    userInfo: state.userInfo,
    isTeamSpace: state.isTeamSpace,
  }));

  useRunTempUploadBatch(
    userInfo,
    'Failed to upload temp images',
    (refs) =>
      getToolTempImagesPendingItems(
        refs,
        createPromptListHelpers(
          () => storeApi.getState().offlineAvatarFormParams.imagePrompts,
          (prompts) => updateOfflineAvatarFormParams('imagePrompts', prompts.filter(Boolean) as FormImagePrompt[]),
        ),
        { imagePrompts },
      ),
    [imagePrompts, updateOfflineAvatarFormParams, userInfo],
  );

  useRunTempUploadBatch(
    userInfo,
    'Failed to upload temp audio',
    (refs) =>
      getToolTempAudioPendingItems(
        refs,
        createPromptListHelpers(
          () => storeApi.getState().offlineAvatarFormParams.audioPrompts,
          (prompts) => updateOfflineAvatarFormParams('audioPrompts', prompts.filter(Boolean) as FormAudioPrompt[]),
        ),
        { audioPrompts },
      ),
    [audioPrompts, updateOfflineAvatarFormParams, userInfo],
  );

  const imagePrompt = imagePrompts[0] || null;
  const audioPrompt = audioPrompts[0] || null;
  const inputMode: OfflineAvatarInputMode = offlineAvatarFormParams.offlineAvatarInputMode ?? 'audio';
  const textPrompt = offlineAvatarFormParams.textPrompt || '';
  const textWeightedLength = useMemo(() => countLipSyncTextWeightedLength(textPrompt), [textPrompt]);
  const avatarHasBuiltInAudio = offlineAvatarHasBuiltInAudio(offlineAvatar);
  const selectedVoiceId = (settings.voice_id as string | undefined)?.trim() || '';
  const resolvedVoiceId = avatarHasBuiltInAudio ? '' : selectedVoiceId;
  const textDuration = estimateLipSyncTextDurationSeconds(textPrompt);
  const audioDuration = Math.max(
    normalizeDiscreteVideoDurationToSeconds(audioPrompt?.prompt?.duration),
    MIN_AUDIO_DURATION,
  );
  const hasSelectedAvatar = !!offlineAvatar?.id;
  const hasSelectedAudio = audioPrompt?.status === 'done' && !!audioPrompt.prompt;
  const hasValidText = textWeightedLength >= MIN_TEXT_LENGTH && textWeightedLength <= MAX_TEXT_LENGTH;
  const hasTimelineText = isTimelineTextValid(textWeightedLength, MAX_TEXT_LENGTH);
  const hasSubmittableAudio = hasSelectedAudio && !!audioPrompt.prompt?.src;
  const timelineActions = offlineAvatarFormParams.offlineAvatarTimeline || [];
  const hasTimelineOverlap = hasTimelineActionOverlap(timelineActions);
  const canEditTimeline =
    inputMode === 'text' ? hasTimelineText && (avatarHasBuiltInAudio || !!resolvedVoiceId) : hasSelectedAudio;
  const inputDuration = inputMode === 'text' ? textDuration : audioDuration;
  const hasTimelineOutOfBounds = hasTimelineActionOutOfBounds(timelineActions, inputDuration);
  const selectedImagePreview = getImagePreview(imagePrompt);
  const selectedHistoryPreview = getOfflineAvatarCreationPreview(selectedHistoryCreation);
  const selectedAvatarPreview = offlineAvatar?.live_image || '';
  const previewMedia = selectedHistoryPreview.url || selectedAvatarPreview || selectedImagePreview;
  const isHistoryVideo = Boolean(selectedHistoryPreview.url) && selectedHistoryPreview.type === 'video';
  const resolution = getSupportedResolution(settings.resolution as string | undefined);
  const imagePromptData = imagePrompt?.prompt;
  const currentCropRatio = imagePromptData?.cropRatio ?? 'free';
  const previewAspectRatio = selectedAvatarPreview
    ? avatarImageLayout.url === selectedAvatarPreview
      ? avatarImageLayout.aspectRatio
      : 9 / 16
    : getPreviewAspectRatio(imagePrompt);
  const cropOptionIndex = OFFLINE_AVATAR_CROP_OPTIONS.findIndex((item) => item.value === cropRatio);
  const cropDefaultIndex = cropOptionIndex >= 0 ? cropOptionIndex : 0;
  const selectedHistoryCreationTask = selectedHistoryCreation?.task;
  const displayedHistoryCreation = useMemo(
    () => (selectedHistoryCreation ? resolveOfflineAvatarHistoryCreation(selectedHistoryCreation, taskList) : null),
    [selectedHistoryCreation, taskList],
  );
  const selectedHistoryTaskFromStore = useMemo(
    () =>
      selectedHistoryTask ? (taskList.find((task) => task.id === selectedHistoryTask.id) ?? selectedHistoryTask) : null,
    [selectedHistoryTask, taskList],
  );
  const selectedHistoryStatusCreationFromStore = useMemo(() => {
    if (!selectedHistoryStatusCreation) return null;

    const task = selectedHistoryTaskFromStore ?? selectedHistoryStatusCreation.task;
    const creation =
      task.creations?.find((item) => item.id === selectedHistoryStatusCreation.id) ?? selectedHistoryStatusCreation;

    return { ...creation, task };
  }, [selectedHistoryStatusCreation, selectedHistoryTaskFromStore]);
  const selectedHistoryTaskState =
    selectedHistoryDisplayTaskState === 'fetchingTaskDetail' &&
    selectedHistoryTaskFromStore?.state === 'success' &&
    !selectedHistoryTaskFromStore.creations?.[0]
      ? selectedHistoryDisplayTaskState
      : selectedHistoryTaskFromStore?.state;
  const selectedHistoryStateMessage =
    selectedHistoryTaskFromStore && !selectedHistoryCreation
      ? getCompactTaskHistoryStateMessage({
          creation: selectedHistoryStatusCreationFromStore ?? undefined,
          task: selectedHistoryTaskFromStore,
          taskState: selectedHistoryTaskState,
        })
      : null;
  const displayedPreviewAspectRatio = selectedHistoryStateMessage ? 16 / 9 : previewAspectRatio;
  const selectedHistoryRecreateTask = selectedHistoryCreationTask ?? selectedHistoryTaskFromStore;
  const canRecreateSelectedHistory = selectedHistoryRecreateTask
    ? !isNoRecreateTask(selectedHistoryRecreateTask.type) &&
      !isRecreateModelVersionUnavailable(
        selectedHistoryRecreateTask.type,
        selectedHistoryRecreateTask.settings?.model_version,
      ) &&
      !isAdmissionTemplateTask(selectedHistoryRecreateTask)
    : false;
  const canSubmitSelectedHistory =
    !!selectedHistoryCreationTask &&
    !isTeamSpace &&
    selectedHistoryCreationTask.extra_params?.is_my_voice !== true &&
    !isAdmissionTemplateTask(selectedHistoryCreationTask) &&
    !isPostDisabledModelVersion(selectedHistoryCreationTask.settings?.model_version) &&
    !selectedHistoryCreation?.err_info?.err_code;

  const clearSelectedHistory = useCallback(() => {
    setSelectedHistoryCreation(null);
    setSelectedHistoryTask(null);
    setSelectedHistoryStatusCreation(null);
    setSelectedHistoryDisplayTaskState(null);
  }, []);

  const handleSelectHistoryCreation = useCallback((creation: CreationWithTask) => {
    setSelectedHistoryCreation(creation);
    setSelectedHistoryTask(creation.task);
    setSelectedHistoryStatusCreation(null);
    setSelectedHistoryDisplayTaskState(null);
  }, []);

  const handleSelectHistoryTask = useCallback(
    (task: TaskInfo, creation?: CreationWithTask, taskState?: DisplayTaskState) => {
      setSelectedHistoryCreation(null);
      setSelectedHistoryTask(task);
      setSelectedHistoryStatusCreation(creation ?? null);
      setSelectedHistoryDisplayTaskState(taskState ?? null);
    },
    [],
  );

  useEffect(() => {
    if (selectedHistoryCreation || !selectedHistoryTaskFromStore) return;

    const creation = selectedHistoryTaskFromStore.creations?.[0];
    if (!creation) return;

    const stateMessage = getCompactTaskHistoryStateMessage({ creation, task: selectedHistoryTaskFromStore });
    if (stateMessage) return;

    setSelectedHistoryCreation({ ...creation, task: selectedHistoryTaskFromStore });
    setSelectedHistoryStatusCreation(null);
    setSelectedHistoryDisplayTaskState(null);
  }, [selectedHistoryCreation, selectedHistoryTaskFromStore]);

  const handleDeleteSelectedHistory = useCallback(() => {
    const creation = selectedHistoryCreation;
    const task = creation?.task;
    if (creation && task) {
      const remaining = (task.creations ?? []).filter((item) => item.id !== creation.id);
      if (remaining.length > 0) {
        updateTask(task.id, { creations: remaining });
      } else {
        removeTask(task.id);
      }
    }
    clearSelectedHistory();
  }, [clearSelectedHistory, removeTask, selectedHistoryCreation, updateTask]);

  useEffect(() => {
    if (settings.resolution !== resolution) {
      updateSettings({ resolution });
    }
  }, [resolution, settings.resolution, updateSettings]);

  useEffect(() => {
    setCropRatio(currentCropRatio);
  }, [currentCropRatio]);

  const clearCropRequest = useCallback(() => {
    setCropModalImageSource(null);
    cropResolverRef.current = null;
    cropRejectRef.current = null;
  }, []);

  const openCropModal = useCallback(
    async (
      imageSource: ImagePromptWithSize | NonNullable<FormImagePrompt['prompt']>,
      nextCropRatio: number | 'free',
    ) => {
      let sourceImageWidth = imageSource.sourceImageWidth || imageSource.selectedImageWidth || 0;
      let sourceImageHeight = imageSource.sourceImageHeight || imageSource.selectedImageHeight || 0;

      if (!sourceImageWidth || !sourceImageHeight) {
        const imageSize = await getImageSize(imageSource.sourceImagePreview || imageSource.sourceImage);
        sourceImageWidth = imageSize.width;
        sourceImageHeight = imageSize.height;
      }

      setCropRatio(nextCropRatio);
      setCropModalImageSource({
        ...imageSource,
        sourceImageWidth,
        sourceImageHeight,
        cropRatio: nextCropRatio,
      } as ImagePromptWithSize);
    },
    [],
  );

  const handleCropClose = useCallback(() => {
    cropResolverRef.current?.({ cancelled: true });
    clearCropRequest();
  }, [clearCropRequest]);

  const handleRemoveAudio = useCallback(() => {
    updateOfflineAvatarFormParams('audioPrompts', []);
    updateOfflineAvatarFormParams('offlineAvatarTimeline', []);
  }, [updateOfflineAvatarFormParams]);

  const handleAvatarImageLoad = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      const { naturalWidth, naturalHeight } = event.currentTarget;
      setAvatarImageLayout({
        url: selectedAvatarPreview,
        aspectRatio: getImageAspectRatio(naturalWidth, naturalHeight),
      });
    },
    [selectedAvatarPreview],
  );

  const handleInputModeChange = useCallback(
    (mode: OfflineAvatarInputMode) => {
      updateOfflineAvatarFormParams('offlineAvatarInputMode', mode);
      updateOfflineAvatarFormParams('offlineAvatarTimeline', []);
    },
    [updateOfflineAvatarFormParams],
  );

  const handleTextPromptChange = useMemoizedFn((value: string) => {
    const trimmed = trimLipSyncTextToMaxWeightedLength(value, MAX_TEXT_LENGTH);
    if (trimmed !== value) {
      toast({
        type: 'warning',
        message: t('create.createForm.error.invalidLength', { limit: MAX_TEXT_LENGTH }),
      });
    }
    updateOfflineAvatarFormParams('textPrompt', trimmed);
  });

  const handleVoiceChange = useCallback(
    (voiceId: string, isMyVoice: boolean) => {
      updateSettings({ voice_id: voiceId });
      const nextExtraParams = { ...offlineAvatarFormParams.extraParams };
      if (isMyVoice) {
        nextExtraParams.is_my_voice = true;
      } else {
        delete nextExtraParams.is_my_voice;
      }
      updateOfflineAvatarFormParams('extraParams', Object.keys(nextExtraParams).length ? nextExtraParams : undefined);
    },
    [offlineAvatarFormParams.extraParams, updateOfflineAvatarFormParams, updateSettings],
  );

  const handleAudioUpload = useMemoizedFn((file: AudioUploadFile) => {
    if (file.status === 'error' || file.status === 'removed') {
      handleRemoveAudio();
      return;
    }

    updateOfflineAvatarFormParams('audioPrompts', [createToolAudioPromptFromUploadFile(file)]);
    if (file.status === 'uploading') {
      updateOfflineAvatarFormParams('offlineAvatarInputMode', 'audio');
    }
    if (file.status === 'done' && file.file?.duration) {
      const duration = Math.max(normalizeDiscreteVideoDurationToSeconds(file.file.duration), MIN_AUDIO_DURATION);
      updateOfflineAvatarFormParams('offlineAvatarTimeline', [
        createTimelineAction(0, Math.min(DEFAULT_TIMELINE_ACTION_DURATION, duration), duration),
      ]);
    }
  });

  const handlePosts = useCallback(
    (creation: CreationInfo) => {
      if (!selectedHistoryCreationTask) return;

      openPostsModal({
        creation: { ...creation, task: selectedHistoryCreationTask },
        mobileNextStep: isMobile,
        onClose: (is_asset) => {
          updateCreation(selectedHistoryCreationTask.id, creation.id, { is_asset });
          setSelectedHistoryCreation((prev) => (prev?.id === creation.id ? { ...prev, is_asset } : prev));
        },
      });
      reportEvent('submit_click', {
        submit_position: 'tools-submit',
      });
    },
    [isMobile, openPostsModal, selectedHistoryCreationTask, updateCreation],
  );

  const handleRecreateSelectedHistory = useCallback(async () => {
    if (!selectedHistoryRecreateTask) return;

    await handleRecreate({
      taskId: selectedHistoryRecreateTask.id,
      creationId: selectedHistoryCreation?.id ?? selectedHistoryStatusCreationFromStore?.id ?? '',
      taskType: selectedHistoryRecreateTask.type,
      settings: selectedHistoryRecreateTask.settings,
      input: selectedHistoryRecreateTask.input,
      templateScene: selectedHistoryRecreateTask.scene,
      interactionPosition: `${selectedHistoryRecreateTask.type}_task_card`,
    });
    clearSelectedHistory();
  }, [
    clearSelectedHistory,
    handleRecreate,
    selectedHistoryCreation,
    selectedHistoryRecreateTask,
    selectedHistoryStatusCreationFromStore,
  ]);

  const resolveCroppedImage = useCallback(
    async (sourceImagePreview: string, range: ImageRange, nextCropRatio: number | 'free') => {
      const imageBlob = await cropImageToBlob(sourceImagePreview, range);
      if (!imageBlob) {
        throw new Error('cropImageFailed');
      }

      return {
        blob: imageBlob,
        width: range[2] - range[0],
        height: range[3] - range[1],
        range,
        cropRatio: nextCropRatio,
      };
    },
    [],
  );

  const getSelectedImage = useCallback(
    async (url: string) => {
      const { width, height } = await getImageSize(url);

      return await new Promise<SelectedImageResult | { cancelled: true }>((resolve, reject) => {
        cropResolverRef.current = resolve;
        cropRejectRef.current = reject;
        void openCropModal(
          {
            sourceImage: url,
            sourceImagePreview: url,
            sourceImageWidth: width,
            sourceImageHeight: height,
            selectedImage: url,
            selectedImagePreview: url,
            selectedImageWidth: width,
            selectedImageHeight: height,
          },
          OFFLINE_AVATAR_DEFAULT_CROP_RATIO,
        );
      });
    },
    [openCropModal],
  );

  const handleCropConfirm = useCallback(
    async (range: ImageRange, selectedCropRatio: number | 'free') => {
      if (!cropModalImageSource) return;

      try {
        if (!cropResolverRef.current && imagePrompt?.prompt) {
          if (
            selectedImageRangeIsSame(range, imagePrompt.prompt.selectedImageRange) &&
            selectedCropRatio === imagePrompt.prompt.cropRatio
          ) {
            clearCropRequest();
            return;
          }

          const result = await handleChangeImageRange({
            range,
            imagePrompt: cropModalImageSource,
            maxImageSize: MAX_IMAGE_SIZE,
            allowTempMode: true,
            userInfo,
          });

          if (!result) {
            if (selectedImageRangeIsSame(range, imagePrompt.prompt.selectedImageRange)) {
              updateOfflineAvatarFormParams('imagePrompts', [
                {
                  ...imagePrompt,
                  status: 'done',
                  prompt: {
                    ...imagePrompt.prompt,
                    cropRatio: selectedCropRatio,
                  },
                },
              ]);
              clearCropRequest();
              return;
            }

            throw new Error('cropImageFailed');
          }

          if (result && 'error' in result && result.error) {
            openConfirmDialog({
              title: t('popup.dialog.error.checkFailed.title'),
              content: result.error.message || t('popup.toast.uploadingError'),
              primaryButtonText: t('popup.dialog.error.confirm'),
            });
            return;
          }

          if (result && 'imagePrompt' in result) {
            const nextPrompt = {
              ...result.imagePrompt,
              cropRatio: selectedCropRatio,
            };
            updateOfflineAvatarFormParams('imagePrompts', [
              {
                ...imagePrompt,
                status: 'done',
                prompt: nextPrompt,
              },
            ]);
          }

          clearCropRequest();
          return;
        }

        const selectedImage = await resolveCroppedImage(
          cropModalImageSource.sourceImagePreview,
          range,
          selectedCropRatio,
        );
        cropResolverRef.current?.(selectedImage);
        clearCropRequest();
      } catch (error) {
        if (cropRejectRef.current) {
          cropRejectRef.current(error);
          return;
        }

        openConfirmDialog({
          title: t('popup.dialog.error.checkFailed.title'),
          content: t('popup.toast.uploadingError'),
          primaryButtonText: t('popup.dialog.error.confirm'),
        });
        clearCropRequest();
      }
    },
    [
      clearCropRequest,
      cropModalImageSource,
      imagePrompt,
      openConfirmDialog,
      resolveCroppedImage,
      t,
      updateOfflineAvatarFormParams,
      userInfo,
    ],
  );

  const handleResolutionChange = useCallback(
    (nextResolution: string) => {
      updateSettings({ resolution: getSupportedResolution(nextResolution) });
    },
    [updateSettings],
  );

  const submitValue = useMemo(() => {
    const filteredFormParams = {
      ...offlineAvatarFormParams,
      imagePrompts: [],
      audioPrompts: inputMode === 'text' ? [] : offlineAvatarFormParams.audioPrompts,
      textPrompt: inputMode === 'text' ? textPrompt : '',
      settings: {
        ...offlineAvatarFormParams.settings,
        duration: inputDuration,
        ...(inputMode === 'text' && !avatarHasBuiltInAudio && resolvedVoiceId ? { voice_id: resolvedVoiceId } : {}),
      },
    };
    return convertToolTask({
      taskType: 'offline_avatar',
      formParams: filteredFormParams,
    });
  }, [avatarHasBuiltInAudio, offlineAvatarFormParams, inputDuration, inputMode, resolvedVoiceId, textPrompt]);

  const disabledTip = useMemo(() => {
    if (inputMode === 'audio' && audioPrompts.some((prompt) => prompt.status === 'uploading')) {
      return t('create.createForm.error.uploadingMedia');
    }
    if (hasTimelineOverlap) {
      return t('tools.offlineAvatar.submit.disabledTimelineOverlap');
    }
    if (hasTimelineOutOfBounds) {
      return t('tools.offlineAvatar.submit.disabledTimelineOutOfBounds');
    }
    if (!hasSelectedAvatar) {
      return t('tools.offlineAvatar.submit.disabledMissingAvatar');
    }
    if (inputMode === 'text' && !hasValidText) {
      return t('tools.offlineAvatar.submit.disabledInvalidText', { max: MAX_TEXT_LENGTH });
    }
    if (inputMode === 'text' && !avatarHasBuiltInAudio && !resolvedVoiceId) {
      return t('tools.offlineAvatar.submit.disabledMissingVoice');
    }
    if (inputMode === 'audio' && (!hasSelectedAudio || (userInfo && !hasSubmittableAudio))) {
      return t('tools.offlineAvatar.submit.disabledMissingAudio');
    }
    return '';
  }, [
    audioPrompts,
    hasValidText,
    hasSelectedAudio,
    hasSubmittableAudio,
    hasTimelineOverlap,
    hasTimelineOutOfBounds,
    inputMode,
    hasSelectedAvatar,
    avatarHasBuiltInAudio,
    resolvedVoiceId,
    t,
    userInfo,
  ]);

  return (
    <MediaDropZone
      id={scrollableTargetId}
      className="mx-auto grid h-full min-h-0 w-full max-w-full grid-cols-[380px_minmax(420px,1fr)_144px] gap-3 px-1 pb-3 sm:flex sm:h-auto sm:min-h-full sm:flex-col sm:gap-3 sm:overflow-y-auto sm:px-3 lg:grid-cols-[380px_minmax(384px,1fr)_144px] lg:pb-3"
      acceptImage={false}
      acceptAudio={true}
      acceptVideo={false}
      maxAudioCount={audioPrompt ? 0 : 1}
      minAudioDuration={MIN_AUDIO_DURATION}
      maxAudioDuration={MAX_AUDIO_DURATION}
      maxAudioSize={MAX_AUDIO_FILE_SIZE}
      disabled={!!audioPrompt}
      allowTempMode={true}
      onChange={(file, type) => {
        if (type === 'audio') handleAudioUpload(file as AudioUploadFile);
      }}
    >
      <aside className="relative flex min-h-0 min-w-0 flex-col overflow-hidden bg-transparent sm:min-h-[min(620px,calc(100svh-24px))] sm:w-full sm:rounded-none sm:bg-transparent">
        <div className="min-h-0 w-[380px] max-w-full flex-1 space-y-6 overflow-y-auto pb-[148px] [scrollbar-width:none] sm:w-full [&::-webkit-scrollbar]:hidden">
          <OfflineAvatarAssetSelector
            title={t('tools.offlineAvatar.avatar.selectionTitle')}
            selectedAvatar={offlineAvatar}
            userId={userInfo?.id}
            maxImageFileSize={MAX_IMAGE_FILE_SIZE}
            getSelectedImage={getSelectedImage}
            onSelect={(avatar) => {
              updateOfflineAvatarFormParams('offlineAvatar', avatar);
              if (avatar) {
                updateOfflineAvatarFormParams('imagePrompts', []);
              }
              clearSelectedHistory();
            }}
          />

          <div className="space-y-6">
            <div className="sm:rounded-16 sm:bg-[#1A1C20] sm:p-3">
              <section className="space-y-3">
                <ToolMediaUploadHeader
                  title={t('tools.offlineAvatar.audioUpload.title')}
                  titleClassName="text-sm"
                  extra={
                    <CustomTooltip content={t('tools.offlineAvatar.audioUpload.titleTooltip')} side="top">
                      <button
                        type="button"
                        className="inline-flex shrink-0 items-center text-system-white48 transition-colors hover:text-system-white"
                        aria-label={t('tools.offlineAvatar.audioUpload.titleTooltipAria')}
                      >
                        <BaseLineInfo className="size-4" />
                      </button>
                    </CustomTooltip>
                  }
                />
                <div className="flex gap-2">
                  {(['text', 'audio'] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      className={`h-10 w-24 shrink-0 rounded-8 border px-4 text-xs transition-colors ${
                        inputMode === mode
                          ? 'border-system-white bg-system-white font-medium text-system-black'
                          : 'border-system-white08 bg-[#1A1C20] text-system-white48 hover:border-system-white16 hover:text-system-white'
                      }`}
                      aria-pressed={inputMode === mode}
                      onClick={() => handleInputModeChange(mode)}
                    >
                      {t(`tools.offlineAvatar.input.${mode}Mode`)}
                    </button>
                  ))}
                </div>
                {inputMode === 'text' ? (
                  <>
                    <div className="relative flex min-h-[120px] w-full flex-col rounded-16 border border-system-white08 bg-system-white04 p-3">
                      <BasicTextarea
                        placeholder={t('tools.offlineAvatar.input.textPlaceholder')}
                        textPrompt={textPrompt}
                        updateTextPrompt={handleTextPromptChange}
                        showClearButton={!!textPrompt}
                        onClearPrompt={() => updateOfflineAvatarFormParams('textPrompt', '')}
                        maxLength={false}
                      />
                    </div>
                    <div className="flex justify-between text-system-white/40 text-xs leading-[1.4]">
                      <span>{t('tools.offlineAvatar.input.duration', { duration: textDuration })}</span>
                      <span>
                        {textWeightedLength}/{MAX_TEXT_LENGTH}
                      </span>
                    </div>
                  </>
                ) : (
                  <ToolAudioUpload
                    audioPrompt={audioPrompt}
                    onAudioUpload={handleAudioUpload}
                    onRemoveAudio={handleRemoveAudio}
                    label={t('tools.offlineAvatar.input.audioMode')}
                    helperText={t('tools.offlineAvatar.audioUpload.helper')}
                    minDuration={MIN_AUDIO_DURATION}
                    maxDuration={MAX_AUDIO_DURATION}
                    maxSize={MAX_AUDIO_FILE_SIZE}
                    showHeader={false}
                    helperTextInsideUpload
                    playbackVariant="cover"
                    surfaceClassName="min-h-[88px] rounded-12 border border-system-white08 bg-[#1A1C20] p-0"
                    className="w-full min-w-0"
                  />
                )}
                {inputMode === 'text' && !avatarHasBuiltInAudio ? (
                  <div className="space-y-2">
                    <span className="text-sm text-system-white">{t('tools.offlineAvatar.input.voice')}</span>
                    <VoiceSelectionDialog
                      open={isVoiceDialogOpen}
                      onOpenChange={setIsVoiceDialogOpen}
                      title={t('tools.offlineAvatar.input.voice')}
                      value={selectedVoiceId}
                      onConfirm={(voiceId, voice) =>
                        handleVoiceChange(voiceId, Boolean(voice && 'is_my' in voice && voice.is_my))
                      }
                      voiceInfoEditFieldSuffix="offline-avatar-input"
                    />
                  </div>
                ) : null}
              </section>
            </div>

            <div className="sm:rounded-16 sm:bg-[#1A1C20] sm:p-3">
              <OfflineAvatarTimelineEditor
                actions={offlineAvatarFormParams.offlineAvatarTimeline || []}
                duration={inputDuration}
                disabled={!canEditTimeline}
                onChange={(actions) => updateOfflineAvatarFormParams('offlineAvatarTimeline', actions)}
              />
            </div>
          </div>
        </div>

        <div className="absolute right-0 bottom-0 left-0 z-10 flex justify-center bg-[linear-gradient(180deg,rgba(2,11,19,0)_0%,#020B13_28%)] px-3 pt-4 pb-6 sm:px-0">
          <div className="flex w-full min-w-0 items-center gap-2.5">
            <OfflineAvatarCombinedSetting resolution={resolution} onChange={handleResolutionChange} />
            <ToolFormSubmitButton
              taskType="offline_avatar"
              submitValue={submitValue}
              creditsText={inputMode === 'text' ? textPrompt : undefined}
              textCreditsMeasuredLength={inputMode === 'text' ? textWeightedLength : undefined}
              disabled={!!disabledTip}
              disabledTip={disabledTip}
              className="h-10 min-w-0 flex-1 rounded-10 px-6 text-sm"
            >
              {t('create.createForm.submit')}
            </ToolFormSubmitButton>
          </div>
        </div>
      </aside>

      <TemplateImgCropModal
        imageSource={cropModalImageSource}
        cropOptions={OFFLINE_AVATAR_CROP_OPTIONS}
        defaultCropIndex={cropDefaultIndex}
        maximizeToAspect
        title={t('imgCropModal.title')}
        description={t('imgCropModal.description')}
        onConfirm={handleCropConfirm}
        onClose={handleCropClose}
      />

      <main className="relative flex min-h-0 min-w-0 flex-col items-center overflow-hidden bg-transparent sm:min-h-[min(680px,calc(100svh-24px))] sm:w-full">
        <div className="flex min-h-0 w-full flex-1 items-center justify-center pb-[76px]">
          <div
            className="relative flex h-full max-h-full min-h-[260px] w-auto max-w-full items-center justify-center overflow-hidden rounded-[20px] bg-[#1A1C20] shadow-2xl"
            style={{ aspectRatio: displayedPreviewAspectRatio }}
          >
            {selectedHistoryStateMessage && selectedHistoryTaskFromStore ? (
              <OfflineAvatarTaskStatePreview
                task={selectedHistoryTaskFromStore}
                stateMessage={selectedHistoryStateMessage}
              />
            ) : previewMedia ? (
              isHistoryVideo ? (
                // biome-ignore lint/a11y/useMediaCaption: user-generated history playback has no captions
                <video
                  src={previewMedia}
                  className="size-full rounded-none object-contain"
                  controls
                  playsInline
                  preload="metadata"
                />
              ) : (
                <img
                  src={previewMedia}
                  alt=""
                  className="size-full rounded-none object-contain"
                  onLoad={
                    selectedAvatarPreview && previewMedia === selectedAvatarPreview ? handleAvatarImageLoad : undefined
                  }
                />
              )
            ) : (
              <OfflineAvatarPlaceholderPreview />
            )}
          </div>
        </div>

        {selectedHistoryCreation || selectedHistoryStateMessage ? (
          <div className="absolute right-0 bottom-0 left-0 z-10 flex h-16 items-center justify-center px-3">
            <div className="flex items-center gap-1 rounded-12 border border-system-white12 bg-[#1A1C20]/95 px-2 py-1.5 text-system-white shadow-[0_8px_24px_rgba(0,0,0,0.24)] backdrop-blur-[20px]">
              {selectedHistoryCreation ? (
                <OfflineAvatarHistoryDetailButton creationId={selectedHistoryCreation.id} />
              ) : null}
              {canRecreateSelectedHistory ? (
                <CustomTooltip content={t('task.action.recreate')} trigger="hover">
                  <Button
                    variant="text"
                    size="original"
                    className="rounded-8 p-1.5 text-inherit text-xl hover:bg-system-hover01"
                    onClick={handleRecreateSelectedHistory}
                  >
                    <BaseLineRecreate className="text-system-white" />
                  </Button>
                </CustomTooltip>
              ) : null}
              {selectedHistoryCreation ? (
                <CreationActionButtons
                  creation={displayedHistoryCreation ?? selectedHistoryCreation}
                  task={displayedHistoryCreation?.task ?? selectedHistoryCreation.task}
                  showActionList={[
                    ...(canSubmitSelectedHistory ? [ActionButtonType.SUBMIT] : []),
                    ActionButtonType.DOWNLOAD,
                    ActionButtonType.SHARE,
                    ActionButtonType.ELLIPSIS_MENU,
                    ActionButtonType.STAR,
                  ]}
                  onSubmit={handlePosts}
                  onSelectedAction={(action: EllipsisActionTypeEnum) => {
                    if (action === EllipsisActionTypeEnum.Report) {
                      openActionDialog({ action, type: 'creation', id: selectedHistoryCreation.id });
                      return;
                    }
                    if (action === EllipsisActionTypeEnum.Delete) {
                      openDeleteDialog({
                        creationId: selectedHistoryCreation.id,
                        taskId: selectedHistoryCreation.task.id,
                        type: 'creation',
                        creationType: selectedHistoryCreation.type,
                        onDelete: handleDeleteSelectedHistory,
                      });
                    }
                  }}
                />
              ) : null}
            </div>
          </div>
        ) : null}
      </main>

      <OfflineAvatarHistoryPanel
        scrollableTargetId={scrollableTargetId}
        onCompactCreationSelect={handleSelectHistoryCreation}
        onCompactTaskSelect={handleSelectHistoryTask}
      />
    </MediaDropZone>
  );
};

export default OfflineAvatarForm;
